/**
 * Participantes da ata: responsáveis vêm do perfil do aluno (contacts.resp).
 * Funcionários demo só como fallback quando a API da turma não retorna ninguém.
 */

/** Fallback offline (demo) — sem inventar parentesco fora do que o perfil demo usa. */
export function buildDemoGuardiansForStudent(student) {
  const last =
    (student.name || "").split(" ").slice(-1)[0] || "Responsável";
  const isAlice = /alice/i.test(student.name || "") || student.ra === "LAB0020";
  if (isAlice) {
    return [
      {
        id: `${student.ra}-mae`,
        name: "Roberta Martins Leite",
        role: "Mãe",
        responsibility: "pedagogico",
        relationship: "Mãe · Responsável acadêmico"
      },
      {
        id: `${student.ra}-pai`,
        name: "Emanuel Mendes Leite",
        role: "Pai",
        responsibility: "financeiro",
        relationship: "Pai · Responsável financeiro"
      }
    ];
  }
  return [
    {
      id: `${student.ra}-mae`,
      name: `Maria ${last}`,
      role: "Mãe",
      responsibility: "pedagogico",
      relationship: "Mãe · Responsável acadêmico"
    },
    {
      id: `${student.ra}-pai`,
      name: `Carlos ${last}`,
      role: "Pai",
      responsibility: "financeiro",
      relationship: "Pai · Responsável financeiro"
    }
  ];
}

/** Funcionários / colaboradores da turma (fallback demo). */
export function buildDemoStaffForClass(classId) {
  const suffix = classId || "turma";
  return [
    {
      id: `staff-coord-ana-${suffix}`,
      name: "Ana Coordenadora Infantil",
      role: "Coordenação"
    },
    {
      id: `staff-coord-${suffix}`,
      name: "José Almeida",
      role: "Coordenação"
    },
    {
      id: `staff-orient-${suffix}`,
      name: "Patrícia Mendes",
      role: "Orientação"
    },
    {
      id: `staff-pedagog-${suffix}`,
      name: "Helena Castro",
      role: "Responsável pedagógico"
    },
    {
      id: `staff-fin-${suffix}`,
      name: "Marcos Vieira",
      role: "Financeiro"
    },
    {
      id: `staff-prof-${suffix}`,
      name: "Ricardo Oliveira",
      role: "Professor"
    },
    {
      id: `staff-sec-${suffix}`,
      name: "Fernanda Dias",
      role: "Secretaria"
    }
  ];
}

/**
 * Extrai tipo de responsabilidade do texto de relacionamento
 * (ex.: "Mãe · Responsável acadêmico" → pedagogico).
 */
export function parseResponsibilityKey(text) {
  const key = String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  if (/financeir/.test(key)) return "financeiro";
  if (/pedagog|academico/.test(key)) return "pedagogico";
  return null;
}

/** Mapeia parentesco/função do perfil para labels da ata. */
function normalizeGuardianRole(roleRaw) {
  const key = String(roleRaw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  // Parentesco puro — responsabilidade vai em campo separado
  if (/^mae$|mother/.test(key)) return "Mãe";
  if (/^pai$|father/.test(key)) return "Pai";
  if (/pedagog|academico/.test(key)) return "Responsável pedagógico";
  if (/financeir/.test(key)) return "Financeiro";
  return roleRaw || "Responsável";
}

export function guardiansFromContacts(student) {
  const resp =
    student &&
    student.contacts &&
    Array.isArray(student.contacts.resp)
      ? student.contacts.resp
      : [];

  return resp
    .map((c, idx) => {
      const name = (c && c.name) || "";
      if (!name.trim()) return null;
      const relationship = String(c.relationship || "Responsável").trim();
      const parts = relationship.split("·").map(p => p.trim()).filter(Boolean);
      const roleRaw = parts[0] || "Responsável";
      const responsibility =
        parseResponsibilityKey(relationship) ||
        parseResponsibilityKey(parts.slice(1).join(" "));
      return {
        id: `${student.ra}-resp-${idx}`,
        name,
        role: normalizeGuardianRole(roleRaw),
        responsibility,
        relationship
      };
    })
    .filter(Boolean);
}

/**
 * Usa só responsáveis do perfil. Não fabrica Avó/Pai/Mãe.
 * @param {object} student
 * @param {{ allowDemoFallback?: boolean }} [opts]
 */
export function enrichStudentWithGuardians(student, opts = {}) {
  const fromProfile = guardiansFromContacts(student);
  let guardians = fromProfile;

  if (!guardians.length && opts.allowDemoFallback) {
    guardians = buildDemoGuardiansForStudent(student);
  }

  return {
    ...student,
    guardians
  };
}

/** Normaliza retorno Academics/teachers (LB usa NOME/CODPROF/FUNCAO). */
export function mapTeacherToStaff(t, idx = 0) {
  const codigo = String(t.CODIGO || t.codigo || "").trim();
  const codProf = String(t.CODPROF || t.codProf || t.id || "").trim();
  const id = String(codProf || codigo || t.codUsuario || `t-${idx}`);
  const name = t.name || t.nome || t.NOME || "";
  const roleRaw =
    t.role ||
    t.cargo ||
    t.FUNCAO ||
    t.funcao ||
    t.disciplina ||
    t.SDISCIPLINA_NOME ||
    "Colaborador";
  const roleKey = String(roleRaw)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  let role = String(roleRaw).trim() || "Colaborador";
  if (/pedagog/.test(roleKey)) role = "Responsável pedagógico";
  if (/financeir/.test(roleKey)) role = "Financeiro";

  return {
    id,
    codigo: codigo || null,
    codProf: codProf || null,
    name: name.trim() || `Funcionário ${idx + 1}`,
    role
  };
}

function normalizePersonName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * Une listas e evita duplicar o operador (Elisa aparece como professor + operador).
 */
export function mergeStaffLists(operator, ...lists) {
  const result = [];
  const seenIds = new Set();
  const seenNames = new Set();
  const opExt = operator
    ? String(operator.externalId || "")
        .trim()
        .toLowerCase()
    : "";
  const opName = operator ? normalizePersonName(operator.name) : "";

  const isOperatorDuplicate = person => {
    if (!operator || !person) return false;
    if (person.id === operator.id) return true;
    const codigo = String(person.codigo || "").toLowerCase();
    const codProf = String(person.codProf || person.id || "").toLowerCase();
    if (opExt && (codigo === opExt || codProf === opExt)) return true;
    if (opName && normalizePersonName(person.name) === opName) return true;
    return false;
  };

  if (operator && operator.id) {
    result.push(operator);
    seenIds.add(operator.id);
    if (opName) seenNames.add(opName);
  }

  lists.flat().forEach(person => {
    if (!person || !person.id) return;
    if (isOperatorDuplicate(person)) return;
    if (seenIds.has(person.id)) return;
    const n = normalizePersonName(person.name);
    if (n && seenNames.has(n)) return;
    seenIds.add(person.id);
    if (n) seenNames.add(n);
    result.push(person);
  });

  return result;
}
